/**
 * Clipboard helpers for macOS rich paste (Apple Pages / Microsoft Word / Preview).
 *
 * On macOS these apps expose a copied document's images ONLY inside the
 * `text/rtf` clipboard flavour (as hex-encoded `\pict` groups) and reference
 * them in the `text/html` flavour by a local path the browser cannot load
 * (`file://` in Chrome/Office, `webkit-fake-url:` in Safari). These pure,
 * DOM/string-only helpers let the paste handler strip those dead image
 * references while keeping formatting, and recover the real image bytes from the
 * RTF so they can be uploaded through the normal image flow.
 *
 * Kept free of any editor/browser-upload dependencies so they can be unit tested.
 */

export interface RtfImage {
    contentType: string;
    extension: string;
    bytes: Uint8Array;
    // Vector metafile (EMF/WMF) the browser/image-server can't store directly —
    // must go through the LibreOffice sidecar (as the Word-import flow does)
    // rather than the plain gallery upload.
    needsConversion: boolean;
}

/**
 * RTF `\pict` sub-types we can recover. Raster types upload straight to the
 * gallery; vector metafiles (EMF/WMF) are flagged for server-side conversion —
 * the same path the Word-import button uses — instead of being dropped.
 */
const rtfPictTypes: Array<{control: string; contentType: string; extension: string; needsConversion: boolean}> = [
    {control: 'pngblip', contentType: 'image/png', extension: 'png', needsConversion: false},
    {control: 'jpegblip', contentType: 'image/jpeg', extension: 'jpg', needsConversion: false},
    {control: 'dibitmap', contentType: 'image/bmp', extension: 'bmp', needsConversion: false},
    {control: 'wbitmap', contentType: 'image/bmp', extension: 'bmp', needsConversion: false},
    {control: 'emfblip', contentType: 'image/x-emf', extension: 'emf', needsConversion: true},
    {control: 'wmetafile', contentType: 'image/x-wmf', extension: 'wmf', needsConversion: true},
];

/**
 * Remove `<img>` tags whose source the browser cannot load: macOS Office/Pages
 * reference clipboard images by local path (`file://`) and Safari uses
 * `webkit-fake-url:`. Only those images are dropped — surrounding text and
 * formatting are preserved. The real image bytes are recovered separately from
 * the RTF flavour by {@link extractRtfImages}.
 */
export function stripUnloadableImages(html: string): string {
    if (!html) {
        return html;
    }

    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    wrap.querySelectorAll('img').forEach(img => {
        const src = img.getAttribute('src') || '';
        if (/^(file:|webkit-fake-url:)/i.test(src)) {
            img.remove();
        }
    });

    return wrap.innerHTML;
}

/**
 * Rewrite each `<img>` the browser can't load (macOS `file://` / Safari
 * `webkit-fake-url:`) to an in-position placeholder, keeping it where it sits in
 * the pasted content. Each rewritten image gets its `src` from `makeSrc(index)`,
 * where `index` counts unloadable images in document order — so the caller can
 * later pair each placeholder with the matching recovered image. Other images
 * and all surrounding markup are left untouched.
 */
export function markUnloadableImages(html: string, makeSrc: (index: number) => string): string {
    if (!html) {
        return html;
    }

    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    let index = 0;
    wrap.querySelectorAll('img').forEach(img => {
        const src = img.getAttribute('src') || '';
        if (/^(file:|webkit-fake-url:)/i.test(src)) {
            img.setAttribute('src', makeSrc(index));
            index += 1;
        }
    });

    return wrap.innerHTML;
}

/**
 * True when the HTML already carries at least one image the browser can load
 * itself (a `data:` URI or an `http(s)` URL). When it does, TinyMCE's own paste
 * inserts it, so the paste handler must NOT also recover images from the RTF or
 * they would be duplicated.
 */
export function htmlHasLoadableImage(html: string): boolean {
    if (!html) {
        return false;
    }

    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    return Array.from(wrap.querySelectorAll('img'))
        .some(img => /^(data:|https?:)/i.test(img.getAttribute('src') || ''));
}

/**
 * Convert a hex string (as stored in RTF `\pict` groups) into raw bytes.
 */
export function hexToBytes(hex: string): Uint8Array {
    const bytes = new Uint8Array(Math.floor(hex.length / 2));
    for (let i = 0; i < bytes.length; i += 1) {
        bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return bytes;
}

/**
 * Remove a balanced `{\*\<control> ...}` destination group (and everything
 * nested inside it) from an RTF string. Used to drop Word's `\*\nonshppict`
 * fallback, which duplicates each shape image as a WMF/EMF alongside the PNG in
 * `\*\shppict` — keeping both would insert every pasted image twice.
 */
function stripDestinationGroup(rtf: string, control: string): string {
    let result = rtf;
    const marker = `{\\*\\${control}`;
    let idx = result.indexOf(marker);
    while (idx !== -1) {
        let depth = 0;
        let end = -1;
        for (let j = idx; j < result.length; j += 1) {
            const c = result[j];
            if (c === '\\') {
                j += 1;
            } else if (c === '{') {
                depth += 1;
            } else if (c === '}') {
                depth -= 1;
                if (depth === 0) {
                    end = j;
                    break;
                }
            }
        }
        if (end === -1) {
            break;
        }
        result = result.slice(0, idx) + result.slice(end + 1);
        idx = result.indexOf(marker);
    }
    return result;
}

/**
 * Extract embedded images from an RTF clipboard payload. Returns one entry per
 * image, in document order — raster images (PNG/JPEG/BMP) flagged for a plain
 * gallery upload and vector metafiles (EMF/WMF) flagged for server-side
 * conversion. Word's `\*\nonshppict` fallback copies are dropped so each image
 * appears once. Never throws on malformed input — it returns whatever it parsed.
 */
export function extractRtfImages(rtf: string): RtfImage[] {
    const images: RtfImage[] = [];
    if (!rtf || rtf.indexOf('\\pict') === -1) {
        return images;
    }

    // Drop Word's non-shape fallback pictures so a PNG (shppict) and its WMF/EMF
    // twin (nonshppict) don't both get inserted.
    const source = stripDestinationGroup(rtf, 'nonshppict');

    let i = 0;
    while (i < source.length) {
        const start = source.indexOf('{\\pict', i);
        if (start === -1) {
            break;
        }

        // Walk to the brace that closes this \pict group, honouring escaped
        // braces (\{ \}) and nested groups (e.g. {\*\blipuid ...}).
        let depth = 0;
        let end = -1;
        for (let j = start; j < source.length; j += 1) {
            const c = source[j];
            if (c === '\\') {
                j += 1; // skip the escaped / control char
            } else if (c === '{') {
                depth += 1;
            } else if (c === '}') {
                depth -= 1;
                if (depth === 0) {
                    end = j;
                    break;
                }
            }
        }

        if (end === -1) {
            break; // unterminated group — stop rather than loop forever
        }

        const image = parsePictGroup(source.slice(start, end + 1));
        if (image) {
            images.push(image);
        }
        i = end + 1;
    }

    return images;
}

/**
 * Parse a single RTF `\pict` group into an image, or null when it is a type we
 * don't recognise or its hex payload is unusable.
 */
function parsePictGroup(group: string): RtfImage | null {
    const type = rtfPictTypes.find(t => group.indexOf(`\\${t.control}`) !== -1);
    if (!type) {
        return null; // unknown pict type — skip.
    }

    // Drop the outer {\pict ... } wrapper, then collapse any nested control
    // groups (\*\blipuid, \*\picprop, …) so their contents can't pollute the hex.
    let body = group.replace(/^\{\\pict/, '').replace(/\}$/, '');
    let previous: string;
    do {
        previous = body;
        body = body.replace(/\{[^{}]*\}/g, ' ');
    } while (body !== previous);

    // Strip RTF control words (\pngblip, \picw123, …) and control symbols.
    body = body.replace(/\\[a-zA-Z]+-?\d*/g, ' ').replace(/\\[^a-zA-Z]/g, ' ');

    const hex = body.replace(/[^0-9a-fA-F]/g, '');
    if (hex.length < 8 || hex.length % 2 !== 0) {
        return null;
    }

    return {
        contentType: type.contentType,
        extension: type.extension,
        bytes: hexToBytes(hex),
        needsConversion: type.needsConversion,
    };
}
